import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Container, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getProduct, getShopByName, convertUrlToShopName } from '../../services/shopService';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import { addToWishlist, removeFromWishlist, isInWishlist } from '../../services/wishlistService';
import Toast from '../../components/Toast';

const ProductDetail = () => {
  const { t } = useTranslation();
  const { shopName, productId } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { currentUser } = useAuth();
  const [product, setProduct] = useState(null);
  const [shop, setShop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  useEffect(() => {
    window.scrollTo(0, 0);
    const loadProductData = async () => {
      try {
        setLoading(true);
        const convertedName = convertUrlToShopName(shopName);
        const shopData = await getShopByName(convertedName);
        if (!shopData) {
          setError('Negozio non trovato');
          return;
        }
        setShop(shopData);

        const productData = await getProduct(shopData.id, productId);
        if (!productData) {
          setError('Prodotto non trovato');
          return;
        }
        setProduct(productData);

        if (currentUser) {
          const inWishlist = await isInWishlist(currentUser.uid, productId);
          setIsWishlisted(inWishlist);
        }
      } catch (err) {
        setError('Errore nel caricamento dei dati del prodotto');
      } finally {
        setLoading(false);
      }
    };
    loadProductData();
  }, [shopName, productId, currentUser]);

  const handleQuantityChange = (value) => {
    const maxQty = product.infiniteStock ? 99 : (product.stock || 1);
    const newQuantity = Math.max(1, Math.min(maxQty, value));
    setQuantity(newQuantity);
  };

  const handleAddToCart = () => {
    if (product && shop) {
      addToCart({
        ...product,
        shopId: shop.id
      }, quantity);
      setToastMessage('L\'articolo è stato aggiunto al carrello');
      setToastVariant('success');
      setShowToast(true);
    }
  };

  const handleWishlistToggle = async () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }

    try {
      setWishlistLoading(true);
      if (isWishlisted) {
        await removeFromWishlist(currentUser.uid, productId);
        setIsWishlisted(false);
        setToastMessage('Articolo rimosso dalla lista dei desideri');
        setToastVariant('success');
      } else {
        const wishlistItem = {
          ...product,
          shopId: shop.id,
          shopName: shop.name
        };
        await addToWishlist(currentUser.uid, wishlistItem);
        setIsWishlisted(true);
        setToastMessage('Articolo aggiunto alla lista dei desideri');
        setToastVariant('success');
      }
      setShowToast(true);
    } catch (err) {
      console.error('Errore nella gestione della wishlist:', err);
      setToastMessage('Errore nella gestione della lista dei desideri');
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setWishlistLoading(false);
    }
  };

  const getAvailabilityInfo = () => {
    if (product.infiniteStock) {
      return { text: 'Disponibilità illimitata', icon: 'bi-infinity', color: '#198754' };
    }
    if (!product.stock || product.stock <= 0) {
      return { text: 'Esaurito', icon: 'bi-x-circle', color: '#dc3545' };
    }
    if (product.stock <= 5) {
      return { text: `Solo ${product.stock} disponibili`, icon: 'bi-exclamation-triangle', color: '#ffc107' };
    }
    return { text: `${product.stock} disponibili`, icon: 'bi-check-circle', color: '#198754' };
  };

  if (loading) return <div className="text-center py-5"><Spinner animation="border" role="status" /></div>;
  if (error || !product) return <div className="text-center py-5 text-danger">{error || 'Prodotto non trovato'}</div>;

  const availability = getAvailabilityInfo();

  return (
    <div className="fade-in">
      <Toast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        variant={toastVariant}
      />
      <Container fluid className="px-0 position-relative" style={{ minHeight: '100vh' }}>
        <div className="d-flex flex-column align-items-center w-100" style={{ maxWidth: 420, margin: '0 auto', paddingBottom: 32 }}>

          {/* Tasto indietro */}
          <div className="w-100 mb-2 px-2">
            <Button
              variant="dark"
              className="rounded-circle shadow-sm border-0"
              style={{ width: 44, height: 44, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
              onClick={() => navigate(`/${shopName}`)}
            >
              <i className="bi bi-arrow-left fs-5 text-white"></i>
            </Button>
          </div>

          {/* Immagine prodotto */}
          <div
            className="mb-3 w-100 position-relative"
            style={{
              borderRadius: 24,
              overflow: 'hidden',
              background: '#fff',
              boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
              height: 340,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img
              src={product.image}
              alt={product.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            {/* Wishlist */}
            <Button
              variant="link"
              className="position-absolute border-0 bg-transparent"
              style={{
                top: 12,
                right: 12,
                padding: '8px 10px',
                color: isWishlisted ? '#dc3545' : '#fff',
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                fontSize: '22px',
                lineHeight: 1,
                boxShadow: 'none'
              }}
              onClick={handleWishlistToggle}
              disabled={wishlistLoading}
            >
              <i className={`bi bi-heart${isWishlisted ? '-fill' : ''}`}></i>
            </Button>
          </div>

          {/* Info prodotto */}
          <div className="w-100 px-3">

            {/* Categoria e disponibilità */}
            <div className="d-flex align-items-center justify-content-between mb-2">
              {product.category && (
                <span
                  className="badge rounded-pill"
                  style={{
                    backgroundColor: '#0A0A17',
                    color: '#fff',
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    padding: '6px 14px'
                  }}
                >
                  {product.category}
                </span>
              )}
              <div className="d-flex align-items-center" style={{ color: availability.color, fontSize: '0.8rem' }}>
                <i className={`bi ${availability.icon} me-1`}></i>
                <span>{availability.text}</span>
              </div>
            </div>

            {/* Nome prodotto */}
            <h4 className="fw-bold mb-1" style={{ fontSize: '1.35rem', lineHeight: 1.3 }}>{product.name}</h4>

            {/* Negozio */}
            <button
              className="btn btn-link text-muted text-decoration-none p-0 mb-3"
              style={{ fontSize: '0.85rem', boxShadow: 'none' }}
              onClick={() => navigate(`/${shopName}`)}
            >
              <i className="bi bi-shop me-1"></i>
              {shop?.name}
            </button>

            {/* Prezzo */}
            <div className="d-flex align-items-baseline mb-3">
              <span className="fw-bold" style={{ fontSize: '1.5rem', color: '#0A0A17' }}>
                €{product.price.toFixed(2)}
              </span>
              {product.price > 0 && (
                <span className="text-muted ms-2" style={{ fontSize: '0.8rem' }}>IVA inclusa</span>
              )}
            </div>

            {/* Descrizione */}
            {product.description && (
              <div className="mb-4">
                <p className="text-muted mb-0" style={{ fontSize: '0.92rem', lineHeight: 1.6 }}>
                  {product.description}
                </p>
              </div>
            )}

            {/* Dettagli aggiuntivi */}
            <div
              className="rounded-4 p-3 mb-4"
              style={{ backgroundColor: '#F8F9FA' }}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>Categoria</span>
                <span className="fw-medium" style={{ fontSize: '0.85rem' }}>{product.category || '-'}</span>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>Disponibilità</span>
                <span className="fw-medium" style={{ fontSize: '0.85rem' }}>
                  {product.infiniteStock ? (
                    <span style={{ color: '#198754' }}><i className="bi bi-infinity me-1"></i>Illimitata</span>
                  ) : (
                    <span>{product.stock || 0} pezzi</span>
                  )}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted" style={{ fontSize: '0.85rem' }}>Venduto da</span>
                <span
                  className="fw-medium text-decoration-underline"
                  style={{ fontSize: '0.85rem', cursor: 'pointer' }}
                  onClick={() => navigate(`/${shopName}`)}
                >
                  {shop?.name}
                </span>
              </div>
            </div>
          </div>

          {/* Selettore quantità e pulsante aggiungi */}
          <div className="d-flex align-items-center justify-content-between w-100 px-3" style={{ gap: 16 }}>
            <div
              style={{
                background: '#F2F2F2',
                borderRadius: 32,
                display: 'flex',
                alignItems: 'center',
                padding: '4px 4px',
                justifyContent: 'center'
              }}
            >
              <Button
                variant="light"
                className="rounded-circle border-0 d-flex align-items-center justify-content-center"
                style={{ width: 44, height: 44, fontSize: 24, padding: 0 }}
                onClick={() => handleQuantityChange(quantity - 1)}
                disabled={quantity <= 1}
              >
                <i className="bi bi-dash"></i>
              </Button>
              <span className="mx-3 fw-bold" style={{ fontSize: 18, minWidth: 24, textAlign: 'center' }}>{quantity}</span>
              <Button
                variant="light"
                className="rounded-circle border-0 d-flex align-items-center justify-content-center"
                style={{ width: 44, height: 44, fontSize: 24, padding: 0 }}
                onClick={() => handleQuantityChange(quantity + 1)}
                disabled={!product.infiniteStock && quantity >= product.stock}
              >
                <i className="bi bi-plus"></i>
              </Button>
            </div>
            <Button
              variant="dark"
              className="rounded-pill d-flex align-items-center justify-content-center shadow-sm border-0"
              style={{ height: 56, minWidth: 170, fontWeight: 600, fontSize: 18, backgroundColor: '#0A0A17' }}
              onClick={handleAddToCart}
              disabled={!product.infiniteStock && (!product.stock || product.stock <= 0)}
            >
              <i className="bi bi-basket2 me-2"></i> Aggiungi
            </Button>
          </div>

        </div>
      </Container>
    </div>
  );
};

export default ProductDetail;